import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { SEO } from "@/components/SEO";
import { fetchArticles, coverImageSrc } from "@/lib/api";

function formatDate(dateStr) {
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function CoverImage({ article, className }) {
  const src = coverImageSrc(article);
  return (
    <div className={`relative overflow-hidden bg-[#F1EFEA] ${className}`}>
      {article.is_featured && (
        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] tracking-wide uppercase text-white bg-[#1C1917]/80 backdrop-blur-sm rounded-full px-2.5 py-1">
          <Star size={10} className="fill-[#F6A041] text-[#F6A041]" /> Featured
        </span>
      )}
      {src ? (
        <img
          src={src}
          alt={article.title}
          loading="lazy"
          style={{ objectPosition: `center ${article.cover_image_focal_y ?? 50}%` }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span className="font-display text-lg text-[#B8B2A7]">Blueprint4Joy</span>
        </div>
      )}
    </div>
  );
}

export default function BlogList() {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    fetchArticles()
      .then(setArticles)
      .catch(() => setArticles([]));
  }, []);

  const lead = articles && articles.length > 0 ? articles[0] : null;
  const rest = articles && articles.length > 1 ? articles.slice(1) : [];

  return (
    <div data-testid="blog-list-page">
      <SEO
        title="This Curious Life"
        description="Personal essays and reflections from Lara Parrish on curiosity, happiness, and the quiet moments that change how we see our lives."
        path="/this-curious-life"
      />
      {/* Editorial hero */}
      <section className="bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-[54px] md:pt-[73px] pb-14 md:pb-16 reveal">
          <p className="text-xs tracking-[0.22em] uppercase text-[#159E9A] mb-6">
            This Curious Life
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1C1917] max-w-2xl">
            Curiosity changes us.
          </h1>
          <div className="mt-6 space-y-4 text-lg text-[#57534E] leading-relaxed max-w-[605px]">
            <p>
              Curiosity has taken me into conversations with strangers,
              unexpected adventures, difficult truths, quiet wonder and a
              deeper understanding of myself.
            </p>
            <p>
              It has also reminded me that beneath almost every hard shell is
              a tender heart, beneath certainty is another perspective and
              beneath ordinary moments are stories worth noticing.
            </p>
            <p>These are a few of mine.</p>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="bg-[#FFFFFF] border-t border-[#E7E5E4]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          {articles === null && (
            <div className="py-16 md:py-20 animate-pulse">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div className="lg:col-span-7 aspect-[4/3] bg-[#F1EFEA]" />
                <div className="lg:col-span-5 space-y-4">
                  <div className="h-3 w-1/3 bg-[#F1EFEA] rounded" />
                  <div className="h-8 w-3/4 bg-[#F1EFEA] rounded" />
                  <div className="h-4 w-full bg-[#F1EFEA] rounded" />
                </div>
              </div>
            </div>
          )}

          {articles?.length === 0 && (
            <div data-testid="blog-empty-state" className="text-center py-20">
              <p className="font-display text-2xl text-[#1C1917]">
                New reflections are on their way.
              </p>
              <p className="text-[#57534E] mt-2">Check back soon.</p>
            </div>
          )}

          {lead && (
            <div className="py-16 md:py-20">
              <Link
                to={`/this-curious-life/${lead.slug}`}
                data-testid={`blog-lead-${lead.slug}`}
                className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
              >
                <div className="lg:col-span-7">
                  <CoverImage article={lead} className="aspect-[4/3]" />
                </div>
                <div className="lg:col-span-5">
                  <p className="text-xs tracking-wide uppercase text-[#57534E] mb-3">
                    {lead.author_name} · {formatDate(lead.date)}
                  </p>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-[42px] leading-[1.12] text-[#1C1917] group-hover:text-[#FF5A4F] transition-colors">
                    {lead.title}
                  </h2>
                  <p className="mt-4 text-base md:text-lg text-[#57534E] leading-relaxed">
                    {lead.excerpt}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#159E9A]">
                    Read the story
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </div>
          )}

          {rest.length > 0 && (
            <div className="divide-y divide-[#E7E5E4] pb-16 md:pb-20">
              {rest.map((a) => (
                <Link
                  key={a.id}
                  to={`/this-curious-life/${a.slug}`}
                  data-testid={`blog-row-${a.slug}`}
                  className="group grid grid-cols-1 sm:grid-cols-12 gap-6 sm:gap-8 py-10 md:py-12 items-center"
                >
                  <div className="sm:col-span-4">
                    <CoverImage article={a} className="aspect-[4/3]" />
                  </div>
                  <div className="sm:col-span-8">
                    <p className="text-xs tracking-wide uppercase text-[#57534E] mb-2">
                      {a.author_name} · {formatDate(a.date)}
                    </p>
                    <h3 className="font-display text-2xl md:text-3xl leading-[1.2] text-[#1C1917] group-hover:text-[#FF5A4F] transition-colors">
                      {a.title}
                    </h3>
                    <p className="mt-2 text-[15px] md:text-base text-[#57534E] leading-relaxed line-clamp-2">
                      {a.excerpt}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#159E9A]">
                      Read the story
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
